import { useState, useRef, useEffect } from 'react';
import { motion } from 'motion/react';
import { Play, X } from 'lucide-react';

const backgroundImage = `${process.env.PUBLIC_URL || ''}/background/12.jpeg`;
const YOUTUBE_EMBED_URL = 'https://www.youtube.com/embed/xXN0FhFRktc?autoplay=1';

const CREAM = '#FFFFF0';

export function VideoSection() {
  const [isPlaying, setIsPlaying] = useState(false);

  const [scrollState, setScrollState] = useState({
    appearProgress: 0,
    disappearProgress: 0,
    overlayProgress: 0,
  });
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const sectionHeight = rect.height;
      const vh = window.innerHeight;

      // Aparecer: al bajar desde el contador, el contenido entra y queda centrado en vista
      const appearProgress = Math.max(0, Math.min(1, 1 - rect.top / vh));

      // Desaparecer: al seguir bajando, Nuestra historia se va para dar paso a El gran día
      const scrollOutStart = sectionHeight * 0.35;
      const scrollOutEnd = sectionHeight * 0.75;
      const disappearProgress = Math.max(
        0,
        Math.min(1, (-rect.top - scrollOutStart) / (scrollOutEnd - scrollOutStart))
      );

      // Transición: al entrar en la sección el crema se va y se ve la imagen de fondo
      const overlayProgress = appearProgress;

      setScrollState({ appearProgress, disappearProgress, overlayProgress });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const { appearProgress, disappearProgress, overlayProgress } = scrollState;
  const contentOpacity = appearProgress * (1 - disappearProgress);
  const cardY = 56 * (1 - appearProgress);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-[100vh] overflow-hidden"
    >
      <div className="sticky top-0 h-screen w-full flex flex-col justify-center items-center px-6">
        <div
          className="section-bg-image absolute inset-0 transition-opacity duration-150 ease-out"
          style={{ backgroundImage: `url(${backgroundImage})` }}
          aria-hidden
        />
        <div
          className="absolute inset-0 z-[1] pointer-events-none transition-opacity duration-200 ease-out"
          style={{ backgroundColor: CREAM, opacity: 1 - overlayProgress }}
          aria-hidden
        />
        <div
          className="relative z-10 w-full max-w-4xl transition-all duration-300 ease-out flex flex-col items-center justify-center"
          style={{
            opacity: contentOpacity,
            transform: `translateY(${cardY}px)`,
          }}
        >
        <div className="text-center mb-8 md:mb-10">
          <h2 className="text-editorial-title text-2xl md:text-3xl tracking-[0.3em] mb-4">
            CAMINO AL ALTAR
          </h2>
          <p
            className="text-lg max-w-2xl mx-auto"
            style={{ color: 'var(--emerald)', opacity: 0.85, fontFamily: 'var(--font-serif)' }}
          >
             Instantes del momento en que empezamos a soñar con este día y que nos trajo hoy a la orilla del mar.
          </p>
        </div>

        <div className="relative w-full">
          <div
            className="rounded-lg overflow-hidden border w-full"
            style={{ borderColor: 'rgba(186, 159, 56, 0.3)', borderWidth: '1px' }}
          >
            <div className="aspect-video relative bg-black/10">
              {!isPlaying ? (
                <>
                  <div
                    className="absolute inset-0 flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(29, 59, 40, 0.06)' }}
                  />
                  <motion.button
                    type="button"
                    onClick={() => setIsPlaying(true)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    className="absolute inset-0 flex items-center justify-center w-full z-10"
                    aria-label="Reproducir video de nuestra historia"
                  >
                    <span
                      className="w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center border-2"
                      style={{ borderColor: 'var(--chartreuse)', backgroundColor: 'var(--butter)' }}
                    >
                      <Play
                        className="w-10 h-10 md:w-12 md:h-12 ml-1"
                        style={{ color: 'var(--emerald)' }}
                      />
                    </span>
                  </motion.button>
                </>
              ) : (
                <>
                  <iframe
                    src={YOUTUBE_EMBED_URL}
                    title="Video nuestra historia"
                    className="absolute inset-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                  <button
                    type="button"
                    onClick={() => setIsPlaying(false)}
                    className="absolute top-2 right-2 z-20 w-9 h-9 rounded-full flex items-center justify-center border transition-colors hover:opacity-90"
                    style={{ backgroundColor: 'var(--butter)', borderColor: 'var(--chartreuse)' }}
                    aria-label="Cerrar video"
                  >
                    <X className="w-5 h-5" style={{ color: 'var(--emerald)' }} />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
    </section>
  );
}
